import React, { useEffect } from 'react';
import {
  useFetchEstimateBreakouts,
  useFetchChangeOrderBreakouts,
  useCreateEstimateBreakoutMutation,
  useUpdateEstimateBreakoutMutation,
  useDeleteEstimateBreakoutMutation,
  useCreateChangeOrderBreakoutMutation,
  useDeleteChangeOrderBreakoutMutation,
  useUpdateChangeOrderBreakoutMutation,
  setPreventAutoCreate,
  useCreateOrUpdateTypicalEstimateBreakoutMutation,
  useCreateOrUpdateTypicalChangeOrderBreakoutMutation,
} from '../../hooks/useFetchBreakout';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { Breakout, TypicalBreakout } from '../../api/protosCompiled/breakout/breakout_pb';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  useTheme,
  alpha,
  Paper,
  styled,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import { Add, Delete, Edit, Save, Cancel, ExpandMore, Close } from '@mui/icons-material';
import { ConfirmationDialog } from '../common/ConfirmationDialog';

// Styled components for consistent theming
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
  padding: theme.spacing(2),
  color: theme.palette.text.primary,
  '&.MuiTableCell-head': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.default, 0.3)
        : alpha(theme.palette.background.default, 0.5),
    fontWeight: 600,
    color: theme.palette.text.primary,
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
}));

// Create a zebra-striped table effect using CSS
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.paper, 0.1)
        : theme.palette.background.paper,
  },
  '&:nth-of-type(even)': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.paper, 0.05)
        : alpha(theme.palette.background.paper, 0.5),
  },
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.action.hover, 0.15)
        : alpha(theme.palette.action.hover, 0.1),
  },
}));

// Edit button component
const EditButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
}));

// Delete button component
const DeleteButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.error.main,
  '&:hover': {
    backgroundColor: alpha(theme.palette.error.main, 0.1),
  },
}));

// Save button component
const SaveButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.success.main,
  '&:hover': {
    backgroundColor: alpha(theme.palette.success.main, 0.1),
  },
}));

// Cancel button component
const CancelButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.warning.main,
  '&:hover': {
    backgroundColor: alpha(theme.palette.warning.main, 0.1),
  },
}));

export default function Breakouts() {
  const theme = useTheme();
  const selectedEstimate = useStore(useUnityBuildStore, state => state.selectedEstimate);
  const selectedChangeOrder = useStore(useUnityBuildStore, state => state.selectedChangeOrder);

  // Add tab state
  const [activeTab, setActiveTab] = React.useState(0);

  const estimateQuery = useFetchEstimateBreakouts(selectedEstimate?.estimateid ?? '');
  const changeOrderQuery = useFetchChangeOrderBreakouts(selectedChangeOrder?.changeorderid ?? '');

  const createEstimateBreakout = useCreateEstimateBreakoutMutation();
  const updateEstimateBreakout = useUpdateEstimateBreakoutMutation();
  const deleteEstimateBreakout = useDeleteEstimateBreakoutMutation();

  const createOrUpdateTypicalEstimateBreakout = useCreateOrUpdateTypicalEstimateBreakoutMutation();
  const createOrUpdateTypicalChangeOrderBreakout =
    useCreateOrUpdateTypicalChangeOrderBreakoutMutation();

  const createChangeOrderBreakout = useCreateChangeOrderBreakoutMutation();
  const updateChangeOrderBreakout = useUpdateChangeOrderBreakoutMutation();
  const deleteChangeOrderBreakout = useDeleteChangeOrderBreakoutMutation();

  const isLoading = selectedEstimate?.estimateid
    ? estimateQuery.isLoading
    : changeOrderQuery.isLoading;
  const isError = selectedEstimate?.estimateid ? estimateQuery.isError : changeOrderQuery.isError;

  const fetchedBreakouts = React.useMemo(() => {
    return selectedEstimate?.estimateid
      ? (estimateQuery.data?.breakoutsList ?? [])
      : (changeOrderQuery.data?.breakoutsList ?? []);
  }, [
    selectedEstimate?.estimateid,
    estimateQuery.data?.breakoutsList,
    changeOrderQuery.data?.breakoutsList,
  ]);

  const [newRows, setNewRows] = React.useState<Breakout.AsObject[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [breakoutToDelete, setBreakoutToDelete] = React.useState<Breakout.AsObject | null>(null);
  const [editableRows, setEditableRows] = React.useState<Record<string, boolean>>({});
  const [tempValues, setTempValues] = React.useState<
    Record<string, Record<string, string | number>>
  >({});
  const [isSaving, setIsSaving] = React.useState(false);

  // Typical breakout dialog state
  const [typicalDialogOpen, setTypicalDialogOpen] = React.useState(false);
  const [selectedBreakoutForTypical, setSelectedBreakoutForTypical] =
    React.useState<Breakout.AsObject | null>(null);
  const [typicalBreakoutName, setTypicalBreakoutName] = React.useState('');
  const [typicalMultipliers, setTypicalMultipliers] = React.useState<Record<string, number>>({});

  // Typical breakout inline editing state
  const [editingTypicalNames, setEditingTypicalNames] = React.useState<Record<string, boolean>>({});
  const [tempTypicalNames, setTempTypicalNames] = React.useState<Record<string, string>>({});
  const [tempTypicalMultipliers, setTempTypicalMultipliers] = React.useState<
    Record<string, Record<string, number>>
  >({});

  // Combine fetched breakouts with new rows for proper rendering
  const allRows = React.useMemo(() => {
    return [...fetchedBreakouts, ...newRows];
  }, [fetchedBreakouts, newRows]);

  // Set preventAutoCreate on mount to avoid unwanted automatic breakout creation
  // This is critical to prevent the auto-creation behavior
  React.useEffect(() => {
    // Set the flag immediately when component mounts
    setPreventAutoCreate(true);

    return () => {
      // Reset when component unmounts
      setPreventAutoCreate(false);
    };
  }, []);

  // Also set preventAutoCreate when estimate or change order changes
  React.useEffect(() => {
    setPreventAutoCreate(true);

    // Clean up any active edits when selection changes
    setNewRows([]);
    setEditableRows({});
    setTempValues({});

    return () => {
      setPreventAutoCreate(false);
    };
  }, [selectedEstimate?.estimateid, selectedChangeOrder?.changeorderid]);

  const handleAddNewRow = () => {
    // Only allow adding one new row at a time
    if (newRows.length > 0) {
      return;
    }

    const newRow: Breakout.AsObject = {
      id: '', //ids are generated on the server
      breakoutname: '',
      multiplier: 1,
      takeoffid: selectedEstimate?.estimateid ?? selectedChangeOrder?.changeorderid ?? '',
      createdbyuserid: '',
      createdbyaccountid: '',
      lastupdated: '',
      typicalbreakoutsList: [],
    };
    setNewRows(prev => [...prev, newRow]);
    // Set this new row as editable
    setEditableRows(prev => ({ ...prev, [newRow.id]: true }));
  };

  const toggleEditMode = (id: string) => {
    setEditableRows(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSave = async (row: Breakout.AsObject, isNew: boolean, rowIndex: number) => {
    if (!row.breakoutname || row.breakoutname.trim() === '') {
      alert('Breakout name cannot be empty');
      return;
    }

    // Check for duplicate names - standard breakout names must be unique
    const trimmedName = row.breakoutname.trim();

    // Check if name conflicts with any existing standard breakout (excluding current row if editing)
    const standardBreakoutConflict = fetchedBreakouts.find(
      breakout =>
        breakout.id !== row.id && // Exclude current row when editing
        breakout.breakoutname.toLowerCase() === trimmedName.toLowerCase()
    );

    if (standardBreakoutConflict) {
      alert(
        `Cannot save breakout: "${trimmedName}" already exists. Standard breakout names must be unique.`
      );
      return;
    }

    // Check if name conflicts with any existing typical breakout
    const typicalBreakoutConflict = fetchedBreakouts.some(breakout =>
      breakout.typicalbreakoutsList?.some(
        typical => typical.breakoutname.toLowerCase() === trimmedName.toLowerCase()
      )
    );

    if (typicalBreakoutConflict) {
      alert(
        `Cannot save breakout: "${trimmedName}" already exists as a typical breakout. Standard breakout names must be unique.`
      );
      return;
    }

    if (isSaving) return; // Prevent multiple save calls
    setIsSaving(true);

    // Prevent auto-creation during save operations
    setPreventAutoCreate(true);

    try {
      // Create a breakout object - standard breakouts always have multiplier = 1
      const breakoutObject = new Breakout();
      breakoutObject.setId(row?.id ?? '');
      breakoutObject.setBreakoutname(row.breakoutname);
      breakoutObject.setMultiplier(1); // Standard breakouts always have multiplier = 1
      breakoutObject.setTakeoffid(row.takeoffid);

      // Save to the server
      if (isNew) {
        if (selectedEstimate?.estimateid) {
          await createEstimateBreakout.mutateAsync(breakoutObject);
        } else if (selectedChangeOrder?.changeorderid) {
          await createChangeOrderBreakout.mutateAsync(breakoutObject);
        }

        // First remove from newRows to prevent duplicates
        setNewRows([]);
      } else {
        if (selectedEstimate?.estimateid) {
          await updateEstimateBreakout.mutateAsync(breakoutObject);
        } else if (selectedChangeOrder?.changeorderid) {
          await updateChangeOrderBreakout.mutateAsync(breakoutObject);
        }
      }

      // Clear temp values and editing state
      setEditableRows({});
      setTempValues({});

      // Force refetch to get updated data
      if (selectedEstimate?.estimateid) {
        await estimateQuery.refetch();
      } else if (selectedChangeOrder?.changeorderid) {
        await changeOrderQuery.refetch();
      }
    } catch (err) {
      console.error('Error saving breakout:', err);
      // Clean up the UI state even on error
      setNewRows([]);
      setEditableRows({});
      setTempValues({});

      // Refetch on error to get the latest data
      if (selectedEstimate?.estimateid) {
        estimateQuery.refetch();
      } else {
        changeOrderQuery.refetch();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = (rowId: string, isNew: boolean, rowIndex: number) => {
    // For new rows, simply remove them and clear all states
    if (isNew) {
      setNewRows([]);
      setEditableRows({});
      setTempValues({});
    } else {
      // For existing rows, just clear the edit state for this row

      // Clear any temporary edits
      setTempValues(prev => {
        const newValues = { ...prev };
        delete newValues[rowId];
        return newValues;
      });

      // Turn off edit mode
      setEditableRows(prev => {
        const newEditable = { ...prev };
        delete newEditable[rowId];
        return newEditable;
      });
    }
  };

  const handleInputChange = (
    id: string,
    field: 'breakoutname' | 'multiplier',
    value: string | number
  ) => {
    // Check if it's a new row or existing row
    const isNew = newRows.some(row => row.id === id);

    if (isNew) {
      // Update the new rows state directly
      setNewRows(prev => prev.map(row => (row.id === id ? { ...row, [field]: value } : row)));
    } else {
      // For existing rows, store temporary values in a separate state
      setTempValues(prev => ({
        ...prev,
        [id]: {
          ...(prev[id] || {}),
          [field]: value,
        },
      }));
    }
  };

  const handleDeleteClick = (breakout: Breakout.AsObject) => {
    if (
      estimateQuery.data?.breakoutsList.length === 1 ||
      changeOrderQuery.data?.breakoutsList.length === 1
    ) {
      alert('At least one breakout is required');
      return;
    }
    setBreakoutToDelete(breakout);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!breakoutToDelete) return;

    const isNew = newRows.some(row => row.id === breakoutToDelete.id);

    if (isNew) {
      setNewRows([]);
    } else {
      // Prevent auto-creation during delete operations
      setPreventAutoCreate(true);
      try {
        if (selectedEstimate?.estimateid) {
          await deleteEstimateBreakout.mutateAsync(breakoutToDelete.id);
          await estimateQuery.refetch();
        } else {
          await deleteChangeOrderBreakout.mutateAsync(breakoutToDelete.id);
          await changeOrderQuery.refetch();
        }
      } catch (err) {
        console.error('Error deleting breakout:', err);
      }
    }
    setDeleteDialogOpen(false);
    setBreakoutToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBreakoutToDelete(null);
  };

  // Typical breakout handlers
  const handleCreateTypicalBreakout = (standardBreakout: Breakout.AsObject) => {
    setSelectedBreakoutForTypical(standardBreakout);
    setTypicalBreakoutName('');
    setTypicalMultipliers({});
    setTypicalDialogOpen(true);
  };

  const handleTypicalDialogClose = () => {
    setTypicalDialogOpen(false);
    setSelectedBreakoutForTypical(null);
    setTypicalBreakoutName('');
    setTypicalMultipliers({});
  };

  const handleTypicalMultiplierChange = (breakoutId: string, multiplier: number) => {
    setTypicalMultipliers(prev => ({
      ...prev,
      [breakoutId]: multiplier || 0,
    }));
  };

  const handleSaveTypicalBreakout = async () => {
    if (!typicalBreakoutName.trim()) {
      alert('Typical breakout name cannot be empty');
      return;
    }

    // Check for duplicate names - typical breakout names must be unique
    const trimmedName = typicalBreakoutName.trim();

    // Check if name conflicts with any standard breakout
    const standardBreakoutConflict = fetchedBreakouts.find(
      breakout => breakout.breakoutname.toLowerCase() === trimmedName.toLowerCase()
    );

    if (standardBreakoutConflict) {
      alert(
        `Cannot create typical breakout: "${trimmedName}" already exists as a standard breakout. Typical breakout names must be unique.`
      );
      return;
    }

    // Check if name conflicts with any existing typical breakout
    const existingTypicalBreakout = fetchedBreakouts.some(breakout =>
      breakout.typicalbreakoutsList?.some(
        typical => typical.breakoutname.toLowerCase() === trimmedName.toLowerCase()
      )
    );

    if (existingTypicalBreakout) {
      alert(
        `Cannot create typical breakout: "${trimmedName}" already exists. Typical breakout names must be unique.`
      );
      return;
    }

    if (!selectedBreakoutForTypical) return;

    setIsSaving(true);
    try {
      // Create typical breakouts for all standard breakouts with multiplier > 0
      const typicalBreakoutsToSave = fetchedBreakouts
        .filter(breakout => typicalMultipliers[breakout.id] > 0)
        .map(breakout => {
          const typicalBreakout = new TypicalBreakout();
          // typicalBreakout.setId(uuidv4()); //ids are generated on the server
          typicalBreakout.setBreakoutid(breakout.id);
          typicalBreakout.setBreakoutname(typicalBreakoutName);
          typicalBreakout.setMultiplier(typicalMultipliers[breakout.id]);
          return typicalBreakout;
        });

      // Save all typical breakouts
      for (const typicalBreakout of typicalBreakoutsToSave) {
        if (selectedEstimate?.estimateid) {
          await createOrUpdateTypicalEstimateBreakout.mutateAsync(typicalBreakout);
        } else if (selectedChangeOrder?.changeorderid) {
          await createOrUpdateTypicalChangeOrderBreakout.mutateAsync(typicalBreakout);
        }
      }

      // Refetch data
      if (selectedEstimate?.estimateid) {
        await estimateQuery.refetch();
      } else if (selectedChangeOrder?.changeorderid) {
        await changeOrderQuery.refetch();
      }

      handleTypicalDialogClose();
    } catch (err) {
      console.error('Error saving typical breakout:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Group typical breakouts by their name (since they share the same name but different standard breakouts)
  const groupedTypicalBreakouts = React.useMemo(() => {
    const groups: Record<
      string,
      {
        name: string;
        items: Array<{
          standardBreakout: Breakout.AsObject;
          typical: TypicalBreakout.AsObject | null;
        }>;
      }
    > = {};

    // First, collect all typical breakout names and their associated standard breakouts
    fetchedBreakouts.forEach(standardBreakout => {
      standardBreakout.typicalbreakoutsList?.forEach(typical => {
        if (!groups[typical.breakoutname]) {
          groups[typical.breakoutname] = {
            name: typical.breakoutname,
            items: [],
          };
        }
      });
    });

    // For each typical breakout group, add all standard breakouts (with their typical data or null)
    Object.keys(groups).forEach(typicalName => {
      fetchedBreakouts.forEach(standardBreakout => {
        const typical =
          standardBreakout.typicalbreakoutsList?.find(t => t.breakoutname === typicalName) || null;
        groups[typicalName].items.push({
          standardBreakout,
          typical,
        });
      });
    });

    return Object.values(groups);
  }, [fetchedBreakouts]);

  // Typical breakout inline editing handlers
  const handleEditTypicalName = (typicalName: string) => {
    setEditingTypicalNames(prev => ({ ...prev, [typicalName]: true }));
    setTempTypicalNames(prev => ({ ...prev, [typicalName]: typicalName }));
  };

  const handleSaveTypicalName = async (oldName: string) => {
    const newName = tempTypicalNames[oldName];
    if (!newName || newName.trim() === '') {
      alert('Typical breakout name cannot be empty');
      return;
    }

    if (newName === oldName) {
      setEditingTypicalNames(prev => ({ ...prev, [oldName]: false }));
      return;
    }

    // Check for duplicate names - typical breakout names must be unique
    const trimmedName = newName.trim();

    // Check if name conflicts with any standard breakout
    const standardBreakoutConflict = fetchedBreakouts.find(
      breakout => breakout.breakoutname.toLowerCase() === trimmedName.toLowerCase()
    );

    if (standardBreakoutConflict) {
      alert(
        `Cannot rename typical breakout: "${trimmedName}" already exists as a standard breakout. Typical breakout names must be unique.`
      );
      return;
    }

    // Check if name conflicts with any existing typical breakout (excluding current name)
    const existingTypicalBreakout = fetchedBreakouts.some(breakout =>
      breakout.typicalbreakoutsList?.some(
        typical =>
          typical.breakoutname.toLowerCase() === trimmedName.toLowerCase() &&
          typical.breakoutname.toLowerCase() !== oldName.toLowerCase()
      )
    );

    if (existingTypicalBreakout) {
      alert(
        `Cannot rename typical breakout: "${trimmedName}" already exists. Typical breakout names must be unique.`
      );
      return;
    }

    setIsSaving(true);
    try {
      // Find all typical breakouts with the old name and update them
      const typicalsToUpdate: TypicalBreakout[] = [];
      fetchedBreakouts.forEach(standardBreakout => {
        standardBreakout.typicalbreakoutsList?.forEach(typical => {
          if (typical.breakoutname === oldName) {
            const updatedTypical = new TypicalBreakout();
            updatedTypical.setId(typical.id);
            updatedTypical.setBreakoutid(typical.breakoutid);
            updatedTypical.setBreakoutname(newName);
            updatedTypical.setMultiplier(typical.multiplier);
            typicalsToUpdate.push(updatedTypical);
          }
        });
      });

      // Save all updated typical breakouts
      for (const typical of typicalsToUpdate) {
        if (selectedEstimate?.estimateid) {
          await createOrUpdateTypicalEstimateBreakout.mutateAsync(typical);
        } else if (selectedChangeOrder?.changeorderid) {
          await createOrUpdateTypicalChangeOrderBreakout.mutateAsync(typical);
        }
      }

      // Refetch data
      if (selectedEstimate?.estimateid) {
        await estimateQuery.refetch();
      } else if (selectedChangeOrder?.changeorderid) {
        await changeOrderQuery.refetch();
      }

      setEditingTypicalNames(prev => ({ ...prev, [oldName]: false }));
      setTempTypicalNames(prev => {
        const newTemp = { ...prev };
        delete newTemp[oldName];
        return newTemp;
      });
    } catch (err) {
      console.error('Error updating typical breakout name:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelTypicalNameEdit = (typicalName: string) => {
    setEditingTypicalNames(prev => ({ ...prev, [typicalName]: false }));
    setTempTypicalNames(prev => {
      const newTemp = { ...prev };
      delete newTemp[typicalName];
      return newTemp;
    });
  };

  // New function to save individual typical multiplier changes
  const handleSaveTypicalMultiplier = (
    typicalName: string,
    standardBreakoutId: string,
    newMultiplier: number
  ) => {
    // Use non-blocking save to prevent UI issues
    setTimeout(async () => {
      try {
        const existingTypical = fetchedBreakouts
          .find(sb => sb.id === standardBreakoutId)
          ?.typicalbreakoutsList?.find(t => t.breakoutname === typicalName);

        // Submit if multiplier > 0 OR if there was an existing typical that we're setting to 0
        if (newMultiplier > 0 || (existingTypical && newMultiplier === 0)) {
          const typical = new TypicalBreakout();
          if (existingTypical) {
            typical.setId(existingTypical.id);
          }
          typical.setBreakoutid(standardBreakoutId);
          typical.setBreakoutname(typicalName);
          typical.setMultiplier(newMultiplier);

          if (selectedEstimate?.estimateid) {
            await createOrUpdateTypicalEstimateBreakout.mutateAsync(typical);
          } else if (selectedChangeOrder?.changeorderid) {
            await createOrUpdateTypicalChangeOrderBreakout.mutateAsync(typical);
          }
        }

        // Refetch data to get the latest state
        if (selectedEstimate?.estimateid) {
          await estimateQuery.refetch();
        } else if (selectedChangeOrder?.changeorderid) {
          await changeOrderQuery.refetch();
        }
      } catch (err) {
        console.error('Error updating typical breakout multiplier:', err);
        // Revert the local state on error
        setTempTypicalMultipliers(prev => {
          const newTemp = { ...prev };
          if (newTemp[typicalName]) {
            delete newTemp[typicalName][standardBreakoutId];
          }
          return newTemp;
        });
      }
    }, 0);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress color="primary" aria-label="Loading breakouts" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
          color: theme.palette.error.main,
        }}
      >
        <Typography variant="body1">Error loading breakouts. Please try again later.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Action buttons - made smaller */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddNewRow}
          size="small"
          sx={{
            textTransform: 'uppercase',
            fontWeight: 600,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              bgcolor: theme.palette.primary.dark,
            },
          }}
          aria-label="Add new standard breakout"
          disabled={isSaving}
        >
          Add Standard
        </Button>

        <Tooltip
          title={
            fetchedBreakouts.length === 0
              ? 'Create a standard breakout first'
              : 'Create typical breakouts with unique names to add multipliers'
          }
          arrow
        >
          <span>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => handleCreateTypicalBreakout(fetchedBreakouts[0])}
              size="small"
              sx={{
                textTransform: 'uppercase',
                fontWeight: 600,
                borderColor: theme.palette.secondary.main,
                color: theme.palette.secondary.main,
                backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                '&:hover': {
                  borderColor: theme.palette.secondary.dark,
                  backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                },
                '&:disabled': {
                  borderColor: alpha(theme.palette.text.disabled, 0.3),
                  color: theme.palette.text.disabled,
                  backgroundColor: 'transparent',
                },
              }}
              aria-label="Add new typical breakout"
              disabled={isSaving || fetchedBreakouts.length === 0}
            >
              Add Typical
            </Button>
          </span>
        </Tooltip>
        {fetchedBreakouts.length === 0 && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.7rem',
              mt: 0.5,
              fontStyle: 'italic',
            }}
          >
            Create a standard breakout first to add typical breakouts
          </Typography>
        )}
      </Box>

      {/* Tab structure for breakouts */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 48,
            },
          }}
        >
          <Tab
            label={`Standard Breakouts (${allRows.length})`}
            id="breakout-tab-0"
            aria-controls="breakout-tabpanel-0"
          />
          <Tab
            label={`Typical Breakouts (${groupedTypicalBreakouts.length})`}
            id="breakout-tab-1"
            aria-controls="breakout-tabpanel-1"
          />
        </Tabs>

        {/* Standard Breakouts Tab Panel */}
        <div
          role="tabpanel"
          hidden={activeTab !== 0}
          id="breakout-tabpanel-0"
          aria-labelledby="breakout-tab-0"
        >
          {activeTab === 0 && (
            <Box sx={{ p: 2 }}>
              <Table
                sx={{
                  width: '100%',
                  tableLayout: 'fixed',
                  backgroundColor: 'transparent',
                }}
                aria-label="Standard breakouts table"
              >
                <TableHead>
                  <TableRow>
                    <StyledTableCell width="80%">Breakout Name</StyledTableCell>
                    <StyledTableCell width="20%">Actions</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allRows.map((row, index) => {
                    const isNew = newRows.some(r => r.id === row.id);
                    const isEditable = editableRows[row.id] || isNew;

                    return (
                      <StyledTableRow
                        key={row.id}
                        sx={{
                          '& td': {
                            position: 'relative',
                            zIndex: isNew ? 2 : 1,
                          },
                        }}
                      >
                        <StyledTableCell>
                          {isEditable ? (
                            <TextField
                              fullWidth
                              variant="outlined"
                              size="small"
                              placeholder="Enter breakout name"
                              value={
                                isNew
                                  ? row.breakoutname
                                  : tempValues[row.id]?.breakoutname !== undefined
                                    ? tempValues[row.id].breakoutname
                                    : row.breakoutname
                              }
                              onChange={e => {
                                e.stopPropagation();
                                handleInputChange(row.id, 'breakoutname', e.target.value);
                              }}
                              onFocus={e => e.stopPropagation()}
                              onBlur={e => e.stopPropagation()}
                              onKeyDown={e => e.stopPropagation()}
                              autoFocus
                              helperText="Standard breakout names must be unique"
                              FormHelperTextProps={{
                                sx: {
                                  color: theme.palette.text.secondary,
                                  fontSize: '0.7rem',
                                  mt: 0.5,
                                },
                              }}
                              InputProps={{
                                sx: {
                                  backgroundColor:
                                    theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.background.paper, 0.1)
                                      : alpha(theme.palette.background.paper, 0.8),
                                  '& .MuiInputBase-input': {
                                    color: theme.palette.text.primary,
                                  },
                                },
                              }}
                            />
                          ) : (
                            <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                              {row.breakoutname}
                            </Typography>
                          )}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {isEditable ? (
                              <>
                                <Tooltip title="Save changes" arrow>
                                  <SaveButton
                                    size="small"
                                    onClick={() =>
                                      handleSave(
                                        {
                                          ...row,
                                          ...(tempValues[row.id] || {}),
                                        },
                                        isNew,
                                        index
                                      )
                                    }
                                    disabled={isSaving}
                                  >
                                    <Save fontSize="small" />
                                  </SaveButton>
                                </Tooltip>
                                <Tooltip title="Cancel changes" arrow>
                                  <CancelButton
                                    size="small"
                                    onClick={() => handleCancelEdit(row.id, isNew, index)}
                                    disabled={isSaving}
                                  >
                                    <Cancel fontSize="small" />
                                  </CancelButton>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <Tooltip title="Edit breakout" arrow>
                                  <EditButton
                                    size="small"
                                    onClick={() => toggleEditMode(row.id)}
                                    disabled={isSaving}
                                  >
                                    <Edit fontSize="small" />
                                  </EditButton>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    row.typicalbreakoutsList && row.typicalbreakoutsList.length > 0
                                      ? `Cannot delete: This breakout has ${row.typicalbreakoutsList.length} associated typical breakout(s) with active counts. Remove all typical breakouts first.`
                                      : 'Delete breakout'
                                  }
                                  arrow
                                >
                                  <span>
                                    <DeleteButton
                                      size="small"
                                      onClick={() => handleDeleteClick(row)}
                                      disabled={
                                        isSaving ||
                                        (row.typicalbreakoutsList &&
                                          row.typicalbreakoutsList.length > 0)
                                      }
                                      sx={{
                                        opacity:
                                          row.typicalbreakoutsList &&
                                          row.typicalbreakoutsList.length > 0
                                            ? 0.5
                                            : 1,
                                      }}
                                    >
                                      <Delete fontSize="small" />
                                    </DeleteButton>
                                  </span>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </div>

        {/* Typical Breakouts Tab Panel */}
        <div
          role="tabpanel"
          hidden={activeTab !== 1}
          id="breakout-tabpanel-1"
          aria-labelledby="breakout-tab-1"
        >
          {activeTab === 1 && (
            <Box sx={{ p: 2 }}>
              {groupedTypicalBreakouts.map((group, index) => (
                <Accordion
                  key={`${group.name}-${index}`}
                  sx={{
                    mb: 1,
                    '&:before': { display: 'none' },
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    borderRadius: 1,
                    '&.Mui-expanded': {
                      margin: '0 0 8px 0',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                      '&.Mui-expanded': {
                        minHeight: 48,
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                        '&.Mui-expanded': {
                          margin: '12px 0',
                        },
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      {editingTypicalNames[group.name] ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <TextField
                            size="small"
                            value={tempTypicalNames[group.name] || group.name}
                            onChange={e => {
                              e.stopPropagation();
                              setTempTypicalNames(prev => ({
                                ...prev,
                                [group.name]: e.target.value,
                              }));
                            }}
                            onFocus={e => e.stopPropagation()}
                            onBlur={e => e.stopPropagation()}
                            onKeyDown={e => e.stopPropagation()}
                            onClick={e => e.stopPropagation()}
                            sx={{ flex: 1, maxWidth: 300 }}
                            helperText="Names must be unique"
                            FormHelperTextProps={{
                              sx: {
                                color: theme.palette.text.secondary,
                                fontSize: '0.65rem',
                                mt: 0.5,
                              },
                            }}
                          />
                          <Tooltip title="Save name" arrow>
                            <SaveButton
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                handleSaveTypicalName(group.name);
                              }}
                              disabled={isSaving}
                            >
                              <Save fontSize="small" />
                            </SaveButton>
                          </Tooltip>
                          <Tooltip title="Cancel edit" arrow>
                            <CancelButton
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                handleCancelTypicalNameEdit(group.name);
                              }}
                              disabled={isSaving}
                            >
                              <Cancel fontSize="small" />
                            </CancelButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                            {group.name}
                          </Typography>
                          <Tooltip title="Edit typical breakout name" arrow>
                            <EditButton
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                handleEditTypicalName(group.name);
                              }}
                              disabled={isSaving}
                            >
                              <Edit fontSize="small" />
                            </EditButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Description</StyledTableCell>
                          <StyledTableCell width="120px">Multiplier</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.items.map(item => (
                          <StyledTableRow key={item.standardBreakout.id}>
                            <StyledTableCell>
                              <Typography variant="body2">
                                {item.standardBreakout.breakoutname}
                              </Typography>
                            </StyledTableCell>
                            <StyledTableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip title="Click to edit multiplier" arrow placement="top">
                                  <TextField
                                    type="number"
                                    size="small"
                                    inputProps={{ min: 0, step: 1 }}
                                    value={
                                      tempTypicalMultipliers[group.name]?.[
                                        item.standardBreakout.id
                                      ] !== undefined
                                        ? tempTypicalMultipliers[group.name][
                                            item.standardBreakout.id
                                          ]
                                        : item.typical?.multiplier || 0
                                    }
                                    onChange={e => {
                                      // Stop event propagation to prevent layout issues
                                      e.stopPropagation();
                                      const newValue = parseInt(e.target.value) || 0;
                                      // Update the local state immediately for responsive UI
                                      setTempTypicalMultipliers(prev => ({
                                        ...prev,
                                        [group.name]: {
                                          ...(prev[group.name] || {}),
                                          [item.standardBreakout.id]: newValue,
                                        },
                                      }));
                                    }}
                                    onFocus={e => {
                                      // Stop event propagation to prevent layout issues
                                      e.stopPropagation();
                                      // Auto-select the entire number when user clicks into the field
                                      e.target.select();
                                    }}
                                    onBlur={e => {
                                      // Stop event propagation to prevent layout issues
                                      e.stopPropagation();

                                      // Save on blur - use a small delay to ensure UI doesn't block
                                      setTimeout(() => {
                                        const currentValue =
                                          tempTypicalMultipliers[group.name]?.[
                                            item.standardBreakout.id
                                          ];
                                        if (
                                          currentValue !== undefined &&
                                          currentValue !== (item.typical?.multiplier || 0)
                                        ) {
                                          handleSaveTypicalMultiplier(
                                            group.name,
                                            item.standardBreakout.id,
                                            currentValue
                                          );
                                        }
                                      }, 50);
                                    }}
                                    onKeyDown={e => {
                                      // Stop event propagation to prevent layout issues
                                      e.stopPropagation();

                                      // Handle Enter key to blur the field and save
                                      if (e.key === 'Enter') {
                                        e.currentTarget.blur();
                                      }
                                    }}
                                    sx={{
                                      width: '100px',
                                      '& .MuiInputBase-input': {
                                        textAlign: 'right',
                                        fontWeight: 600,
                                        color:
                                          (item.typical?.multiplier || 0) > 0
                                            ? theme.palette.primary.main
                                            : theme.palette.text.disabled,
                                        '&:disabled': {
                                          color: theme.palette.text.disabled,
                                        },
                                        cursor: 'text',
                                      },
                                      // Highlight when value has changed
                                      '& .MuiOutlinedInput-root': {
                                        borderColor:
                                          tempTypicalMultipliers[group.name]?.[
                                            item.standardBreakout.id
                                          ] !== undefined &&
                                          tempTypicalMultipliers[group.name][
                                            item.standardBreakout.id
                                          ] !== (item.typical?.multiplier || 0)
                                            ? theme.palette.warning.main
                                            : 'inherit',
                                        transition: 'border-color 0.2s ease-in-out',
                                        '&:hover': {
                                          borderColor: theme.palette.primary.main,
                                        },
                                        '&.Mui-focused': {
                                          borderColor: theme.palette.primary.main,
                                          borderWidth: '2px',
                                        },
                                      },
                                    }}
                                  />
                                </Tooltip>
                              </Box>
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </AccordionDetails>
                </Accordion>
              ))}
              {groupedTypicalBreakouts.length === 0 && (
                <Box
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    border: `2px dashed ${alpha(theme.palette.divider, 0.3)}`,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.background.paper, 0.5),
                  }}
                >
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                    No typical breakouts created yet
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    Create typical breakouts to add multipliers to your standard breakouts
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </div>
      </Paper>

      {/* Typical Breakout Creation Dialog */}
      <Dialog
        open={typicalDialogOpen}
        onClose={handleTypicalDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Create Typical Breakout
            </Typography>
            <IconButton onClick={handleTypicalDialogClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter typical breakout name"
              value={typicalBreakoutName}
              onChange={e => {
                e.stopPropagation();
                setTypicalBreakoutName(e.target.value);
              }}
              onFocus={e => e.stopPropagation()}
              onBlur={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
              autoFocus
              sx={{ mb: 2 }}
              helperText="Typical breakout names must be unique and cannot match standard breakout names"
              FormHelperTextProps={{
                sx: {
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                },
              }}
            />
          </Box>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Description</StyledTableCell>
                  <StyledTableCell width="120px">Multiplier</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fetchedBreakouts.map(breakout => (
                  <StyledTableRow key={breakout.id}>
                    <StyledTableCell>
                      <Typography variant="body2">{breakout.breakoutname}</Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <TextField
                        type="number"
                        size="small"
                        inputProps={{ min: 0, step: 1 }}
                        value={typicalMultipliers[breakout.id] || 0}
                        onChange={e => {
                          e.stopPropagation();
                          handleTypicalMultiplierChange(breakout.id, parseInt(e.target.value) || 0);
                        }}
                        onFocus={e => e.stopPropagation()}
                        onBlur={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        sx={{ width: '100px' }}
                      />
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleTypicalDialogClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTypicalBreakout}
            disabled={
              isSaving ||
              !typicalBreakoutName.trim() ||
              Object.values(typicalMultipliers).every(multiplier => !multiplier || multiplier <= 0)
            }
          >
            {isSaving ? <CircularProgress size={20} /> : 'Create Typical Breakout'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        title="Delete Breakout"
        message={`Are you sure you want to delete "${breakoutToDelete?.breakoutname}"?`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
}
